import React, { useCallback, useEffect, useState } from 'react';
import { get } from 'lodash';
import { useFieldArray } from 'react-hook-form';
import {
  ATMButton,
  ATMField,
  ATMGrid,
  ATMHeader,
  ATMTable,
  ATMInput,
} from 'shared-it-appmod-ui';
import Lang from 'src/libraries/language';
import {
  IRequestFormMethods,
  IRequestLandingZone,
} from 'src/models/request.model';
import { useFileContext } from 'src/contexts/file.context';
import FileUploader from 'src/components/atoms/file-uploader/file-uploader.component';
import { SettingKey } from 'src/constants';
import { useSetting } from 'src/hooks/setting.hook';
import { ToastSuccess } from 'src/components/atoms/toaster/toaster.component';
import Confirm from 'src/components/atoms/confirm/confirm.component';
import LabelRequired from 'src/components/atoms/label-required/label-required.component';

type IProps = IRequestFormMethods & {
  category;
};

const FlightRequestConstructionLandingZoneForm: React.FC<IProps> = ({
  category,
  control,
  errors,
}) => {
  const { actions } = useFileContext();
  const maxfield = useSetting(SettingKey.MaxWorkLocation, 4);
  const [isConfirm, setIsConfirm] = useState<Partial<IRequestLandingZone>>();
  const [coordinates, setCoordinates] = useState<
    Record<string, string | undefined>
  >({});

  const { fields, append, remove, insert } = useFieldArray<IRequestLandingZone>(
    {
      control,
      name: `${category}.landingInfos`,
    }
  );

  const handleAdd = useCallback(() => {
    append({ coordinate: '', file: undefined });
  }, [append]);

  useEffect(() => {
    if (
      !fields.length &&
      get(control, `defaultValuesRef.current.${category}.landingInfos`, [])
        .length === 0
    ) {
      handleAdd();
    } else {
      fields.forEach((value) => {
        setCoordinates((values) => ({
          ...values,
          [value.id ?? '']: value.coordinate,
        }));
      });
    }
  }, [control, category, fields, setCoordinates, handleAdd]);

  const handleUpload = useCallback(
    async (id, item, isDelete = false) => {
      const index = fields.findIndex((value) => value.id === id);

      if (isDelete) {
        const { file, uploadId, ...data } = fields[index];
        remove(index);
        insert(index, { coordinate: coordinates[id] ?? '', ...data });

        if (uploadId) {
          const result = await actions.dataDELETE(uploadId);

          if (result.payload) {
            ToastSuccess(Lang.MSG_FILE_DELETE_SUCESS);
          }
        }
      } else {
        const data = {
          ...fields[index],
        };

        remove(index);
        insert(index, {
          ...data,
          coordinate: coordinates[id] ?? '',
          file: item,
        });
      }
    },
    [fields, coordinates, actions, remove, insert]
  );

  const handleRemove = useCallback(
    async (index, file) => {
      if (file && file.fileName) {
        const result = await actions.uploadDELETE(file.fileName);

        if (result.payload && file.uploadId) {
          await actions.dataDELETE(file.uploadId);
        }
      }

      remove(index);
      setIsConfirm(undefined);
    },
    [actions, remove, setIsConfirm]
  );

  const handleCoordinate = useCallback(
    (id, value) => {
      setCoordinates((values) => ({ ...values, [id]: value }));
    },
    [setCoordinates]
  );

  return (
    <>
      <ATMHeader size="small" className="text-underline">
        {Lang.TTL_LANDING_ZONE_COORDINATES}
      </ATMHeader>

      <ATMGrid>
        <ATMGrid.Column width={10}>
          <ATMTable>
            <ATMTable.Header>
              <ATMTable.Row>
                <ATMTable.HeaderCell width={6}>
                  <LabelRequired>
                    {Lang.LBL_LANDING_ZONE_COORDINATE}
                  </LabelRequired>
                </ATMTable.HeaderCell>
                <ATMTable.HeaderCell width={8}>
                  {Lang.LBL_ATTACHMENTS}
                </ATMTable.HeaderCell>
                <ATMTable.HeaderCell width={2} />
              </ATMTable.Row>
            </ATMTable.Header>
            <ATMTable.Body>
              {fields.map((item, i) => (
                <ATMTable.Row key={item.id}>
                  <ATMTable.Cell>
                    {item.landingId && (
                      <ATMField
                        key={`landingId_${category}_${item.id}`}
                        name={`${category}.landingInfos[${i}].landingId`}
                        defaultValue={item.landingId}
                        className="hidden"
                        as={ATMInput}
                        control={control}
                        type="hidden"
                      />
                    )}
                    <ATMField
                      key={`coordinate_${category}_${item.id}`}
                      as={ATMInput}
                      name={`${category}.landingInfos[${i}].coordinate`}
                      control={control}
                      defaultValue={item.coordinate}
                      placeholder={Lang.LBL_ENTER}
                      error={get(
                        errors,
                        `${category}.landingInfos[${i}].coordinate`,
                        false
                      )}
                      onChange={([_, { value }]) => {
                        handleCoordinate(item.id, value);

                        return value;
                      }}
                    />
                  </ATMTable.Cell>
                  <ATMTable.Cell verticalAlign="middle">
                    {item.file && (
                      <>
                        {item.file.uploadId && (
                          <ATMField
                            key={`uploadId_${category}_${item.id}`}
                            name={`${category}.landingInfos[${i}].file.uploadId`}
                            defaultValue={item.uploadId}
                            className="hidden"
                            as={ATMInput}
                            control={control}
                            type="hidden"
                          />
                        )}

                        <ATMField
                          key={`uploadTypeId_${category}_${item.id}`}
                          name={`${category}.landingInfos[${i}].file.uploadTypeId`}
                          defaultValue={item.file?.uploadTypeId}
                          className="hidden"
                          as={ATMInput}
                          control={control}
                          type="hidden"
                        />
                        <ATMField
                          key={`filename_${category}_${item.id}`}
                          name={`${category}.landingInfos[${i}].file.fileName`}
                          defaultValue={item.file?.fileName}
                          className="hidden"
                          as={ATMInput}
                          control={control}
                          type="hidden"
                        />
                        <ATMField
                          key={`docName_${category}_${item.id}`}
                          name={`${category}.landingInfos[${i}].file.docName`}
                          defaultValue={item.file?.docName}
                          className="hidden"
                          as={ATMInput}
                          control={control}
                          type="hidden"
                        />
                      </>
                    )}

                    <div id={`${category}.landingInfos[${i}].file.fileName`}>
                      <FileUploader
                        key={`uploader_${category}_${item.id}`}
                        file={item.file}
                        error={get(
                          errors,
                          `${category}.landingInfos[${i}].file.fileName`,
                          false
                        )}
                        text={Lang.LBL_UPLOAD_MAP}
                        onSuccess={(file) => handleUpload(item.id, file)}
                        onDelete={(file) => handleUpload(item.id, file, true)}
                      />
                    </div>
                  </ATMTable.Cell>
                  <ATMTable.Cell textAlign="right">
                    {fields.length > 1 && (
                      <ATMButton
                        icon="trash alternate outline"
                        type="button"
                        onClick={() =>
                          item.landingId
                            ? setIsConfirm(item)
                            : handleRemove(i, item.file)
                        }
                      />
                    )}
                  </ATMTable.Cell>
                </ATMTable.Row>
              ))}
            </ATMTable.Body>
            <ATMTable.Footer>
              <ATMTable.Row>
                <ATMTable.HeaderCell colSpan={4}>
                  <ATMButton
                    secondary
                    icon="plus"
                    type="button"
                    disabled={fields.length >= parseInt(maxfield, 10)}
                    onClick={handleAdd}
                    content={Lang.LBL_ADD}
                  />
                </ATMTable.HeaderCell>
              </ATMTable.Row>
            </ATMTable.Footer>
          </ATMTable>

          <Confirm
            open={!!isConfirm}
            size="tiny"
            onCancel={() => setIsConfirm(undefined)}
            header={Lang.TTL_CONFIRM_DELETE}
            content={Lang.formatString(
              Lang.MSG_CONFIRM_DELETE,
              isConfirm?.coordinate ?? ''
            )}
            onConfirm={() => {
              if (isConfirm) {
                const index = fields.findIndex(
                  (value) => value.landingId === isConfirm.landingId
                );
                handleRemove(index, isConfirm.file);
              }
            }}
          />
        </ATMGrid.Column>
      </ATMGrid>
    </>
  );
};

export default FlightRequestConstructionLandingZoneForm;
